import { directorSkillSelectionFromState, type DirectorSkillSelection } from '../components/chat/directorSkillSelection'
import { directorSkillSerializedMarkers } from '../api/directorSkills'
import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import NavbarV2 from '../components/landing-v2/NavbarV2'
import HeroSectionV2 from '../components/landing-v2/HeroSectionV2'
import WorkflowSectionV2 from '../components/landing-v2/WorkflowSectionV2'
import CapabilitiesSectionV2 from '../components/landing-v2/CapabilitiesSectionV2'
import InspirationSectionV2 from '../components/landing-v2/InspirationSectionV2'
import FooterV2 from '../components/landing-v2/FooterV2'
import { useCreateProject, useRecentProjects } from '../api/hooks'
import { api } from '../api/client'
import {
  composePromptWithDocuments,
  pendingDocumentLabel,
  settlePendingDocument,
  type DocumentAttachment,
  type PendingDocument,
} from '../components/shared/documentPrompt'
import type { CreateProjectRequest } from '../api/types'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import { normalizeLocale } from '../i18n/supportedLocales'
import { createNadDebug } from '../utils/debugLog'
import { getFileSizeError, type ClassifiedMediaFile } from '../utils/uploadFileValidation'
import { NO_STYLE_ID, type VisualStyle } from '../constants/styles'
import {
  DEFAULT_ASPECT_RATIO,
  type AspectRatio,
  type ProductionTarget,
} from '../components/landing/hero-content'
import { UPFRONT_PROJECT_PLAN_ENABLED } from '../constants/featureFlags'
import { CONTENT_LANGUAGE_AUTO } from '../i18n/contentLanguages'
import ProjectPlanModal, {
  EMPTY_PROJECT_PLAN_DRAFT,
  projectPlanDraftFromUnknown,
  type ProjectPlanDraft,
} from '../components/landing-v2/ProjectPlanModal'
import {
  createLandingUploadId,
  landingUploadedReferencesFromUnknown,
  prepareLandingReferenceHandoff,
  MAX_LANDING_IMAGE_UPLOADS,
  type LandingGenerateOptions,
  type LandingUploadReference,
  type LandingUploadType,
  type LandingUploadedReference,
  type LandingUploadFailure,
} from '../components/landing/landingUploads'
import { toast } from 'sonner'
import {
  DEFAULT_PROJECT_SETUP_STATE,
  projectSetupChangedCount,
  projectSetupCommitProps,
  trackLandingProjectSetupAdvancedToggled,
  trackLandingPromptOptionAction,
  type ProjectSetupState,
} from '../components/landing-v2/landingAnalytics'

const isLocalDev =
  import.meta.env.DEV &&
  !import.meta.env.VITE_ENABLE_AUTH &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

// NAD feature tag: landing top-panel aspect ratio -> project creation.
const aspectRatioLog = createNadDebug('dw:landing-aspect-ratio:flow')
// NAD feature tag: landing asset upload -> project creation (shared with v1).
const landingUploadLog = createNadDebug('dw:landing-upload:flow')
// NAD feature tag: v1 upfront project-plan modal -> create payload. Mirrors the
// backend `feature="upfront-project-plan"` so the modal->create->persist chain
// reads as one timeline.
const projectPlanLog = createNadDebug('dw:upfront-project-plan')
const skillLog = createNadDebug('dw:director-skills:landing')
const styleLog = createNadDebug('dw:no-style-onboarding')

type LandingLocationState = {
  selectedSkill?: DirectorSkillSelection
  prompt?: string
  style?: VisualStyle
  productionTarget?: ProductionTarget
  aspectRatio?: AspectRatio
  initialUploadReferences?: LandingUploadedReference[]
  initialReferencedNodeIds?: string[]
  // v1 upfront project plan captured before the /login redirect (logged-out
  // resume). Threaded through the full round-trip so a filled plan is not lost.
  planDraft?: ProjectPlanDraft
}

/**
 * The plan-derived subset of the create payload. Only fields the user actually
 * set are included — empty story fields, the 'auto' language sentinel and a
 * null duration are omitted (output quality is never sent: the plan does not
 * offer it, so the backend create-time default applies), so an unopened plan
 * (or the flag-off path) sends nothing (ship-dark-safe, opt-in wire contract).
 */
function planPayloadFields(plan: ProjectPlanDraft): CreateProjectRequest {
  const fields: CreateProjectRequest = {}
  const title = plan.title.trim()
  if (title) fields.name = title
  const logline = plan.logline.trim()
  if (logline) fields.logline = logline
  const storyline = plan.storyline.trim()
  if (storyline) fields.storyline = storyline
  const tone = plan.tone.trim()
  if (tone) fields.tone = tone
  const structure = plan.structure.trim()
  if (structure) fields.structure = structure
  if (plan.contentLanguage !== CONTENT_LANGUAGE_AUTO) {
    fields.content_language = plan.contentLanguage
  }
  if (plan.targetDurationS !== null) fields.target_duration_s = plan.targetDurationS
  return fields
}

/** True when the plan carries any field the create payload would send. Drives
 * the hero "Plan set" indicator. */
function isPlanMeaningful(plan: ProjectPlanDraft): boolean {
  return Object.keys(planPayloadFields(plan)).length > 0
}

/**
 * Compose the Director's first-turn prompt: the hero prompt LEADS as the
 * headline idea; the plan block (logline / premise / tone / structure / target
 * duration) is appended as elaboration when present, never overwriting the hero
 * text (§6.2 precedence). Returns the hero prompt unchanged when the plan is
 * empty.
 */
export function composeFirstTurnPrompt(heroPrompt: string, plan: ProjectPlanDraft): string {
  const lines: string[] = []
  if (plan.logline.trim()) lines.push(`Logline: ${plan.logline.trim()}`)
  if (plan.storyline.trim()) lines.push(`Premise: ${plan.storyline.trim()}`)
  if (plan.tone.trim()) lines.push(`Tone: ${plan.tone.trim()}`)
  if (plan.structure.trim()) lines.push(`Structure: ${plan.structure.trim()}`)
  if (plan.targetDurationS !== null) lines.push(`Target duration: ~${plan.targetDurationS}s`)
  if (lines.length === 0) return heroPrompt
  return `${heroPrompt}\n\n--- Project plan (starting guidance) ---\n${lines.join('\n')}`
}

function stringField(value: object, key: string): string | undefined {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  return typeof entry?.[1] === 'string' ? entry[1] : undefined
}

function productionTargetValue(value: string | undefined): ProductionTarget | undefined {
  if (value === 'webtoon' || value === 'video') return value
  return undefined
}

function aspectRatioValue(value: string | undefined): AspectRatio | undefined {
  if (value === '16:9' || value === '9:16') return value
  return undefined
}

function stringArrayField(value: object, key: string): string[] | undefined {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  if (!Array.isArray(entry?.[1])) return undefined
  return entry[1].filter((item): item is string => typeof item === 'string')
}

function objectField(value: object, key: string): object | undefined {
  const entry = Object.entries(value).find(([entryKey]) => entryKey === key)
  const field = entry?.[1]
  return field && typeof field === 'object' ? field : undefined
}

export function landingLocationState(
  value: object | null | undefined,
): LandingLocationState | null {
  if (!value || typeof value !== 'object') return null
  return {
    prompt: stringField(value, 'prompt'),
    selectedSkill: directorSkillSelectionFromState(value, 'selectedSkill'),
    style: stringField(value, 'style'),
    productionTarget: productionTargetValue(stringField(value, 'productionTarget')),
    aspectRatio: aspectRatioValue(stringField(value, 'aspectRatio')),
    initialUploadReferences: landingUploadedReferencesFromUnknown(
      objectField(value, 'initialUploadReferences'),
    ),
    initialReferencedNodeIds: stringArrayField(value, 'initialReferencedNodeIds'),
    planDraft: projectPlanDraftFromUnknown(objectField(value, 'planDraft')) ?? undefined,
  }
}

interface LandingPageV2Props {
  heroVideoEnabled?: boolean
  // Flag test-seam: read once from UPFRONT_PROJECT_PLAN_ENABLED at the module
  // boundary (default) and passed down as a boolean so tests set it via prop
  // (no vi.mock / ISOLATED_TEST_FILES churn). Gates the hero "Settings" button
  // + the plan modal mount.
  planEnabled?: boolean
}

/**
 * Landing page v2 — now the primary landing page at "/".
 *
 * Full project creation flow: style library, aspect ratio, auth-gated redirect,
 * shared project creation behavior.
 */
export default function LandingPageV2({
  heroVideoEnabled = true,
  planEnabled = UPFRONT_PROJECT_PLAN_ENABLED,
}: LandingPageV2Props = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const createProject = useCreateProject()
  const { t, i18n } = useTranslation('pages')
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const resumedRef = useRef(false)

  const [currentStyle, setCurrentStyle] = useState<VisualStyle>(NO_STYLE_ID)
  const [currentAspectRatio, setCurrentAspectRatio] = useState<AspectRatio>(DEFAULT_ASPECT_RATIO)
  const [planDraft, setPlanDraft] = useState<ProjectPlanDraft>(EMPTY_PROJECT_PLAN_DRAFT)
  const [isPlanOpen, setIsPlanOpen] = useState(false)
  const [landingUploads, setLandingUploads] = useState<LandingUploadReference[]>([])
  const productionTarget: ProductionTarget = 'video'
  const canUseLandingUploads = isAuthenticated || isLocalDev
  const showRecentProjects = !isAuthLoading && isAuthenticated
  const recentProjectsQuery = useRecentProjects(showRecentProjects)
  const uploadsRef = useRef<LandingUploadReference[]>([])

  useEffect(() => {
    uploadsRef.current = landingUploads
  }, [landingUploads])

  // Revoke object URLs on unmount to avoid leaking blob previews.
  useEffect(() => {
    return () => {
      for (const upload of uploadsRef.current) {
        URL.revokeObjectURL(upload.previewUrl)
      }
    }
  }, [])

  // Enqueue a whole picker selection at once. Enforces the image cap against the
  // authoritative `uploadsRef` (updated synchronously below) so rapid or batched
  // selections cannot exceed the cap, and only allocates blob URLs for accepted
  // files (no leak for dropped over-cap files). Only images are capped;
  // video/audio are unbounded.
  const handleLandingUploadFiles = useCallback((files: File[], type: LandingUploadType) => {
    if (files.length === 0) return
    const current = uploadsRef.current
    let accepted = files
    if (type === 'image') {
      const imageCount = current.filter((upload) => upload.type === 'image').length
      const remaining = Math.max(0, MAX_LANDING_IMAGE_UPLOADS - imageCount)
      accepted = files.slice(0, remaining)
    }
    const droppedByImageCap = files.length - accepted.length
    const validFiles = accepted.filter((file) => {
      const sizeError = getFileSizeError(file.size, type)
      if (!sizeError) return true

      const message = i18n.t(`canvas:nodeError.${sizeError.code}`, { ...sizeError.params })
      landingUploadLog({
        event: 'file-size-rejected-before-request',
        upload_type: type,
        file_size_bytes: file.size,
        max_size_bytes: sizeError.params.maxMb * 1024 * 1024,
      })
      toast.error(message, { id: `landing-upload-too-large-${type}` })
      return false
    })
    const rejectedBySize = accepted.length - validFiles.length
    const newRefs: LandingUploadReference[] = validFiles.map((file) => ({
      id: createLandingUploadId(type),
      type,
      file,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
      status: 'queued',
    }))
    landingUploadLog({
      event: 'queued-batch',
      upload_type: type,
      selected_count: files.length,
      accepted_count: newRefs.length,
      dropped_count: droppedByImageCap,
      size_rejected_count: rejectedBySize,
      image_total:
        current.filter((upload) => upload.type === 'image').length +
        (type === 'image' ? newRefs.length : 0),
      max: MAX_LANDING_IMAGE_UPLOADS,
    })
    if (newRefs.length > 0) {
      // Update the ref synchronously (authoritative) then mirror into state, so a
      // subsequent selection reads the post-batch count regardless of React flush
      // timing and unmount cleanup can revoke the just-added blob URLs.
      uploadsRef.current = [...current, ...newRefs]
      setLandingUploads(uploadsRef.current)
    }
    if (type === 'image' && droppedByImageCap > 0) {
      toast.error(t('landing.uploadsMaxReached', { count: MAX_LANDING_IMAGE_UPLOADS }), {
        id: 'landing-uploads-max',
      })
    }
  }, [i18n, t])

  const handleLandingUploadFile = useCallback((file: File, type: LandingUploadType) => {
    handleLandingUploadFiles([file], type)
  }, [handleLandingUploadFiles])

  // Merged "Upload media" picker: one mixed selection, routed per type through
  // handleLandingUploadFiles so the image cap, size checks and queued-batch log
  // apply exactly as before. Types are visited in first-seen order.
  const handleLandingUploadMedia = useCallback(
    (accepted: ClassifiedMediaFile[], rejected: File[]) => {
      const byType = new Map<LandingUploadType, File[]>()
      for (const { file, type } of accepted) {
        const group = byType.get(type)
        if (group) group.push(file)
        else byType.set(type, [file])
      }
      for (const [type, files] of byType) {
        handleLandingUploadFiles(files, type)
      }
      if (rejected.length > 0) {
        toast.error(
          t('shared:uploadFlyout.unsupportedIgnored', {
            names: rejected.map((file) => file.name).join(', '),
          }),
          { id: 'landing-upload-unsupported' },
        )
      }
    },
    [handleLandingUploadFiles, t],
  )

  // Attached documents (RFC-1218). Extraction happens HERE, at attach time,
  // against the project-less route — not after createProject — so a refusal
  // (a scan with no text layer, an unreadable encoding, a PR2 content-safety
  // block) is shown before a project has been made. Deferring it would strand
  // an empty project the user never asked for.
  const [landingDocuments, setLandingDocuments] = useState<DocumentAttachment[]>([])
  // Every in-flight extraction, not just the latest. A single `pending` string
  // is wrong once two documents are attached in quick succession: the first to
  // settle would clear it while the second was still extracting, and Create
  // would then ship without the second document. Tracked by id so duplicate
  // filenames cannot cancel each other out.
  const [pendingDocuments, setPendingDocuments] = useState<readonly PendingDocument[]>([])
  const pendingDocumentId = useRef(0)
  const [documentError, setDocumentError] = useState<string | null>(null)
  const documentPending = pendingDocumentLabel(pendingDocuments)

  const handleUploadLandingDocument = useCallback(async (file: File) => {
    setDocumentError(null)
    const id = ++pendingDocumentId.current
    setPendingDocuments((previous) => [...previous, { id, name: file.name }])
    try {
      const extracted = await api.extractLandingDocumentText(file)
      setLandingDocuments((previous) => [
        ...previous,
        {
          type: 'document',
          filename: extracted.filename,
          kind: extracted.kind,
          text: extracted.text,
          charCount: extracted.char_count,
          segmentCount: extracted.segment_count,
          clearedImageCount: extracted.cleared_image_count,
        },
      ])
    } catch (error) {
      // Surface the backend's specific refusal verbatim — a generic "upload
      // failed" would hide the one piece of information that tells the user
      // what to do next (re-export as UTF-8, use a file with a text layer, …).
      setDocumentError(error instanceof Error ? error.message : String(error))
    } finally {
      setPendingDocuments((previous) => settlePendingDocument(previous, id))
    }
  }, [])

  const handleRemoveLandingDocument = useCallback((index: number) => {
    setLandingDocuments((previous) => previous.filter((_, i) => i !== index))
  }, [])

  const handleRemoveLandingUpload = useCallback((id: string) => {
    if (isCreating) return
    const removed = uploadsRef.current.find((upload) => upload.id === id)
    if (removed) URL.revokeObjectURL(removed.previewUrl)
    // Synchronously update the authoritative ref (mirrors the add path) so the
    // removed entry cannot be recounted or reintroduced before the next render.
    uploadsRef.current = uploadsRef.current.filter((upload) => upload.id !== id)
    setLandingUploads(uploadsRef.current)
    landingUploadLog({
      event: 'landing-upload:removed', node_id: id,
      trace_id: `landing-reference:${id}`,
      remaining_count: uploadsRef.current.filter((upload) => upload.type === 'image').length,
      max: MAX_LANDING_IMAGE_UPLOADS,
    })
  }, [isCreating])

  const setLandingUploadStatus = useCallback((
    id: string,
    status: LandingUploadReference['status'],
    errorMessage?: string,
  ) => {
    setLandingUploads((uploads) => uploads.map((upload) => (
      upload.id === id ? { ...upload, status, error: errorMessage } : upload
    )))
  }, [])

  const uploadLandingReferences = useCallback(async (
    projectId: string,
    uploads: LandingUploadReference[],
  ): Promise<{ uploaded: LandingUploadedReference[]; failures: LandingUploadFailure[] }> => {
    const uploaded: LandingUploadedReference[] = []
    const failures: LandingUploadFailure[] = []
    for (const upload of uploads) {
      setLandingUploadStatus(upload.id, 'uploading')
      landingUploadLog({
        event: 'upload-start',
        upload_id: upload.id,
        node_id: upload.id,
        trace_id: `landing-reference:${upload.id}`,
        upload_type: upload.type,
        name: upload.name,
      })
      try {
        // Files attached to the landing composer's prompt.
        const result = await api.uploadAsset(projectId, upload.file, upload.name, 'prompt_attachment')
        uploaded.push({
          id: upload.id,
          type: upload.type,
          name: result.filename || upload.name,
          path: result.path,
          size: result.size,
        })
        setLandingUploadStatus(upload.id, 'uploaded')
        landingUploadLog({ event: 'upload-complete', upload_id: upload.id, node_id: upload.id, trace_id: `landing-reference:${upload.id}`, path: result.path })
      } catch (err) {
        // Non-fatal: keep uploading the rest and return the successes. The
        // project is already created, so aborting here would orphan it and lose
        // the user's prompt. Failed items keep their 'error' status.
        const message = err instanceof Error ? err.message : 'Upload failed'
        failures.push({ id: upload.id, message })
        setLandingUploadStatus(upload.id, 'error', message)
        landingUploadLog({ event: 'upload-error', upload_id: upload.id, error: message })
        toast.error(message)
      }
    }
    return { uploaded, failures }
  }, [setLandingUploadStatus])

  const handleGenerate = useCallback(
    // Overrides let the post-login resume pass the values captured before the
    // redirect. Without them, this closure would read the stale (default)
    // `currentStyle`/`currentAspectRatio` because React state updates are async.
    async (
      prompt: string,
      options: LandingGenerateOptions = {},
      styleOverride?: VisualStyle,
      aspectRatioOverride?: AspectRatio,
      planOverride?: ProjectPlanDraft,
    ) => {
      if (isCreating) return

      const style = styleOverride ?? currentStyle
      const aspectRatio = aspectRatioOverride ?? currentAspectRatio
      // Resolved plan: the resume path passes it explicitly (setPlanDraft above
      // hasn't flushed yet), otherwise the current draft. Only consumed when the
      // feature is enabled (ship dark).
      const plan = planOverride ?? planDraft
      const uploads = options.uploads ?? []
      const documents = options.documents ?? []
      if (options.selectedSkill) {
        const skill = options.selectedSkill
        const markerAtPosition = directorSkillSerializedMarkers(skill).some(
          (marker) => prompt.slice(skill.position, skill.position + marker.length) === marker,
        )
        if (!markerAtPosition) {
          throw new Error('Landing skill binding does not match its inline marker')
        }
      }
      skillLog({ event: 'landing-skill-handoff', feature: 'director-skills',
        destination: !isAuthenticated && !isLocalDev ? 'login' : 'workspace',
        skill_ref: options.selectedSkill?.ref ?? null,
        skill_version_id: options.selectedSkill?.versionId ?? null,
        inline_position: options.selectedSkill?.position ?? null,
      })

      if (!isAuthenticated && !isLocalDev) {
        // Queued uploads require an authenticated project to attach to — surface
        // the sign-in prompt instead of silently dropping them on redirect.
        if (uploads.length > 0) {
          setError(t('landing.signInBeforeUpload'))
          return
        }
        aspectRatioLog({
          event: 'landing-generate-redirect-login',
          aspect_ratio: aspectRatio,
          style,
          production_target: productionTarget,
        })
        navigate('/login', {
          state: {
            returnTo: '/',
            prompt,
            style,
            productionTarget,
            aspectRatio,
            // Thread the plan through the /login round-trip so a filled plan is
            // not dropped on resume (LoginPage forwards it via a field-agnostic
            // spread; landingLocationState re-parses it on return).
            planDraft: plan,
            selectedSkill: options.selectedSkill,
          },
        })
        return
      }

      setIsCreating(true)
      setError(null)

      // Plan-derived payload fields + composed first-turn prompt — only when the
      // feature is enabled, so the wire contract is unchanged while dark.
      const planFields = planEnabled ? planPayloadFields(plan) : {}
      const plannedPrompt = planEnabled ? composeFirstTurnPrompt(prompt, plan) : prompt
      // Document text rides in the FIRST DIRECTOR TURN, never in the
      // createProject payload: `prompt` there is capped at
      // CREATIVE_TEXT_MAX_LENGTH (10 000), which is the exact ceiling this
      // feature exists to lift.
      const composedPrompt = composePromptWithDocuments(plannedPrompt, documents)

      try {
        styleLog({ event: 'landing_create', style_id: style })
        aspectRatioLog({
          event: 'landing-create-project',
          aspect_ratio: aspectRatio,
          style,
          production_target: productionTarget,
        })
        projectPlanLog({
          event: 'landing-create-with-plan',
          feature: 'upfront-project-plan',
          plan_enabled: planEnabled,
          has_plan_fields: Object.keys(planFields).length > 0,
          sent_name: planFields.name !== undefined,
          content_language: planFields.content_language ?? null,
          target_duration_s: planFields.target_duration_s ?? null,
          composed_prompt_len: composedPrompt.length,
        })
        const project = await createProject.mutateAsync({
          prompt,
          production_target: productionTarget,
          style,
          aspect_ratio: aspectRatio,
          // UI locale so the backend can title the AI-generated project name
          // in the user's language (#2340). Explicit param — S1-1 (never read
          // locale from JWT user_metadata).
          ui_locale: normalizeLocale(i18n.language) ?? 'en',
          ...planFields,
        })
        styleLog({ event: 'landing_created', project_id: project.id, style_id: style })

        // Upload queued assets to the created project BEFORE navigating, so the
        // workspace can seed the upload nodes before the initial Director turn.
        // Uploads are best-effort: on failure we still navigate to the created
        // project (never orphan it) with whatever uploaded successfully.
        const { uploaded: uploadReferences, failures } = await uploadLandingReferences(project.id, uploads)
        const handoff = prepareLandingReferenceHandoff(prompt, options, uploadReferences, failures)
        landingUploadLog({
          event: 'landing-reference-handoff:prepared',
          project_id: project.id,
          referenced_node_ids: handoff.referencedNodeIds ?? [],
          failed_upload_ids: failures.map((failure) => failure.id),
          uploaded_count: uploadReferences.length,
          success_count: uploadReferences.length,
          explicit_reference_count: options.referencedNodeIds?.length ?? 0,
          failed_count: failures.length,
        })
        for (const uploadId of handoff.referencedNodeIds ?? []) {
          landingUploadLog({
            event: 'landing-reference-handoff:reference-ready',
            project_id: project.id,
            upload_id: uploadId,
            node_id: uploadId,
            trace_id: `landing-reference:${uploadId}`,
            mentioned: options.referencedNodeIds?.includes(uploadId) ?? false,
            boundary: options.referencedNodeIds?.includes(uploadId) ? 'inline-reference' : 'queued-upload',
          })
        }

        navigate(`/project/${project.id}/workspace`, {
          state: {
            // Composed first-turn prompt (hero prompt leads + plan block) seeds
            // the Director's first turn via AgentChat.startWithPrompt (§6.1).
            initialPrompt: composePromptWithDocuments(
              planEnabled ? composeFirstTurnPrompt(handoff.prompt, plan) : handoff.prompt,
              documents,
            ),
            initialSelectedSkill: handoff.selectedSkill,
            initialUploadReferences: uploadReferences,
            initialReferencedNodeIds: handoff.referencedNodeIds,
          },
        })
      } catch (err) {
        setError(err instanceof Error ? err.message : t('landing.failedToCreateProject'))
        setIsCreating(false)
      }
    },
    [isCreating, isAuthenticated, currentStyle, currentAspectRatio, planDraft, planEnabled, productionTarget, navigate, createProject, uploadLandingReferences, t, i18n.language],
  )

  // Resume after login redirect
  const locationState = landingLocationState(location.state)
  const pendingResume = isAuthenticated && !!locationState?.prompt && !resumedRef.current
  useEffect(() => {
    const prompt = locationState?.prompt
    if (pendingResume && prompt) {
      resumedRef.current = true
      if (locationState.style) setCurrentStyle(locationState.style)
      if (locationState.aspectRatio) setCurrentAspectRatio(locationState.aspectRatio)
      if (locationState.planDraft) setPlanDraft(locationState.planDraft)
      // Pass the resumed values explicitly — `handleGenerate` would otherwise
      // read the stale default state (the setters above haven't flushed yet).
      // Queued upload Files can't survive the login navigation, so only the
      // already-referenced node IDs (if any) are carried across.
      handleGenerate(
        prompt,
        { referencedNodeIds: locationState.initialReferencedNodeIds, selectedSkill: locationState.selectedSkill },
        locationState.style,
        locationState.aspectRatio,
        locationState.planDraft ?? undefined,
      )
    }
  }, [pendingResume]) // eslint-disable-line react-hooks/exhaustive-deps

  // RFC-1577 project_setup analytics: latest committed draft / orientation and
  // the state at the moment the Project setup modal opened (see below).
  const planDraftRef = useRef(planDraft)
  const aspectRatioRef = useRef(currentAspectRatio)
  const planSessionStartRef = useRef<ProjectSetupState | null>(null)

  const handleStyleChange = useCallback((style: VisualStyle) => {
    setCurrentStyle(style)
  }, [])

  const handleAspectRatioChange = useCallback((ratio: AspectRatio) => {
    aspectRatioRef.current = ratio
    setCurrentAspectRatio(ratio)
  }, [])

  // RFC-1577 `landing_prompt_option_action` (project_setup). The modal
  // live-applies edits and Cancel restores its open snapshot via onChange
  // BEFORE onClose, so the refs below hold the committed state at close time:
  // one `open` on open, one `change`/`reset` on close iff the session committed
  // anything (never per keystroke or per control).
  useEffect(() => {
    planDraftRef.current = planDraft
  }, [planDraft])
  useEffect(() => {
    aspectRatioRef.current = currentAspectRatio
  }, [currentAspectRatio])

  const handlePlanDraftChange = useCallback((next: ProjectPlanDraft) => {
    planDraftRef.current = next
    setPlanDraft(next)
  }, [])

  const handleOpenPlan = useCallback(() => {
    const start: ProjectSetupState = { draft: planDraftRef.current, aspectRatio: aspectRatioRef.current }
    planSessionStartRef.current = start
    setIsPlanOpen(true)
    trackLandingPromptOptionAction({
      option_type: 'project_setup',
      action: 'open',
      was_default_before: projectSetupChangedCount(DEFAULT_PROJECT_SETUP_STATE, start) === 0,
      is_authenticated: isAuthenticated,
    })
  }, [isAuthenticated])

  const handleClosePlan = useCallback(() => {
    const start = planSessionStartRef.current
    planSessionStartRef.current = null
    setIsPlanOpen(false)
    if (!start) return
    const commit = projectSetupCommitProps(
      start,
      { draft: planDraftRef.current, aspectRatio: aspectRatioRef.current },
      isAuthenticated,
    )
    if (commit) trackLandingPromptOptionAction(commit)
  }, [isAuthenticated])

  const handlePlanAdvancedToggle = useCallback(
    (expanded: boolean, hasAdvancedContent: boolean) => {
      trackLandingProjectSetupAdvancedToggled({
        expanded,
        has_advanced_content: hasAdvancedContent,
        is_authenticated: isAuthenticated,
      })
    },
    [isAuthenticated],
  )

  // Show a loading screen while we auto-resume a pending prompt after login
  if (pendingResume || (isCreating && locationState?.prompt)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-8 w-8 text-[#8dd5d8]" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
              fill="none"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="text-white/60 font-[Fustat]">{t('landing.creatingProject')}</span>
        </div>
      </div>
    )
  }

  return (
    <div data-page="landing" className="min-h-screen bg-black text-white overflow-x-clip">
      <NavbarV2 />
      <HeroSectionV2
        onGenerate={handleGenerate}
        isCreating={isCreating}
        videoEnabled={heroVideoEnabled}
        selectedStyle={currentStyle}
        onStyleChange={handleStyleChange}
        selectedAspectRatio={currentAspectRatio}
        onAspectRatioChange={handleAspectRatioChange}
        productionTarget={productionTarget}
        uploads={landingUploads}
        onUploadFile={canUseLandingUploads ? handleLandingUploadFile : undefined}
        onUploadMedia={canUseLandingUploads ? handleLandingUploadMedia : undefined}
        onRemoveUpload={handleRemoveLandingUpload}
        documents={landingDocuments}
        documentPending={documentPending}
        documentError={documentError}
        onUploadDocument={canUseLandingUploads ? handleUploadLandingDocument : undefined}
        onRemoveDocument={handleRemoveLandingDocument}
        planEnabled={planEnabled}
        onOpenPlan={handleOpenPlan}
        isPlanSet={planEnabled && isPlanMeaningful(planDraft)}
        projectSetupChangedCount={planEnabled
          ? projectSetupChangedCount(DEFAULT_PROJECT_SETUP_STATE, { draft: planDraft, aspectRatio: currentAspectRatio })
          : undefined}
        recentProjects={showRecentProjects ? {
          projects: recentProjectsQuery.data?.data ?? [],
          isLoading: recentProjectsQuery.isLoading,
          error: recentProjectsQuery.error,
          onRetry: () => void recentProjectsQuery.refetch(),
        } : undefined}
      />
      {planEnabled && (
        <ProjectPlanModal
          isOpen={isPlanOpen}
          onClose={handleClosePlan}
          draft={planDraft}
          onChange={handlePlanDraftChange}
          // Orientation is surfaced INSIDE the modal when the flag is on (the
          // inline hero aspect pill is hidden). Same single owner
          // (currentAspectRatio) + live-commit handler as the inline pill — no
          // state fork, and Create/login-resume keep reading currentAspectRatio.
          selectedAspectRatio={currentAspectRatio}
          onAspectRatioChange={handleAspectRatioChange}
          onAdvancedToggle={handlePlanAdvancedToggle}
        />
      )}
      <WorkflowSectionV2 />
      <CapabilitiesSectionV2 />
      <InspirationSectionV2 />
      <FooterV2 />

      {error && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-4 py-2 rounded-lg shadow z-50">
          {error}
          <button onClick={() => setError(null)} className="ml-2 opacity-70 hover:opacity-100">
            ×
          </button>
        </div>
      )}
    </div>
  )
}
